import { useCallback, useEffect, useState } from "react";
import myAxios from "../../utils/axiosWrapper";
import getConfig from "../../conf/config";

const PERIOD_DAYS = { "7d": 7, "14d": 14, "30d": 30 };

// Server-side aggregation: one request returns the whole Insights payload for
// the period + filters (see backend api/insights_utils.py).
export const useInsightsData = ({
  period = "30d",
  team = "all",
  department = "all",
  direction = "all",
} = {}) => {
  const [data, set_data] = useState(null);
  const [loading, set_loading] = useState(true);
  const [error, set_error] = useState(null);
  const [reload_key, set_reload_key] = useState(0);

  const refetch = useCallback(() => set_reload_key((key) => key + 1), []);

  useEffect(() => {
    let cancelled = false;
    set_loading(true);
    set_error(null);

    const params = { days: PERIOD_DAYS[period] || 30, direction };
    if (team && team !== "all") params.team_id = team;
    if (department && department !== "all") params.department_id = department;

    myAxios
      .get(`${getConfig().API_URL}/audio/api/v1/insights/summary`, { params })
      .then((response) => {
        if (cancelled) return;
        set_data(response?.data || null);
        set_loading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Insights: summary fetch failed", err);
        set_error(err?.response?.data?.detail || err?.message || "Erreur de chargement");
        set_loading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [period, team, department, direction, reload_key]);

  return { data, loading, error, refetch };
};

// Latest stored action plan + on-demand (re)generation.
export const useInsightsRecommendations = () => {
  const [doc, set_doc] = useState(null);
  const [loading, set_loading] = useState(true);
  const [generating, set_generating] = useState(false);
  const [error, set_error] = useState(null);

  useEffect(() => {
    let cancelled = false;
    myAxios
      .get(`${getConfig().API_URL}/audio/api/v1/insights/recommendations`)
      .then((response) => {
        if (cancelled) return;
        set_doc(response?.data?.recommendations ? response.data : null);
        set_loading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Insights: recommendations fetch failed", err);
        set_error(err?.response?.data?.detail || err?.message || "Erreur de chargement");
        set_loading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const generate = useCallback(async () => {
    set_generating(true);
    set_error(null);
    try {
      const response = await myAxios.post(
        `${getConfig().API_URL}/audio/api/v1/insights/recommendations/generate`
      );
      set_doc(response?.data || null);
    } catch (err) {
      console.error("Insights: recommendations generation failed", err);
      set_error(err?.response?.data?.detail || err?.message || "Erreur de génération");
    } finally {
      set_generating(false);
    }
  }, []);

  return { doc, loading, generating, error, generate };
};

// Real teams / departments for the filter bar.
export const useOrganisations = () => {
  const [organisations, set_organisations] = useState({ teams: [], departments: [] });

  useEffect(() => {
    let cancelled = false;
    myAxios
      .get(`${getConfig().API_URL}/audio/api/v1/admin/company_organisations`)
      .then((response) => {
        if (cancelled) return;
        const payload = response?.data?.[0] || {};
        set_organisations({
          teams: payload.teams || [],
          departments: payload.departments || [],
        });
      })
      .catch((err) => {
        console.error("Insights: organisations fetch failed", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return organisations;
};
